/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Helmet} from 'react-helmet'
import {Box, Button, Flex, IconButton, Text, Image} from '@chakra-ui/react'

import {BrandLogoBlack} from '../icons'
import useMultiSite from '../../hooks/use-multi-site'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {CONTACT_US_HREF, HOME_HREF} from '../../constants'

// hardcoded translations only for DE & FR
const translations = {
    default: {
        pageTitle: "This page isn't working",
        title: 'Page not found',
        subheadline: 'Oops, something went wrong here!',
        paragraph:
            'The page you were looking for was not found. You can return to our homepage or contact customer support.',
        button_homepage: 'Go to homepage',
        button_customer_support: 'Customer Support'
    },
    de: {
        pageTitle: 'Diese Seite funktioniert leider nicht',
        title: 'Seite nicht gefunden',
        subheadline: 'Ups, hier ist etwas schiefgegangen!',
        paragraph:
            'Die gesuchte Seite konnte leider nicht gefunden werden. Besuche stattdessen unsere Homepage oder kontaktiere den Kundenservice.',
        button_homepage: 'Zur Homepage',
        button_customer_support: 'Zum Kundenservice'
    },
    fr: {
        pageTitle: 'Cette page ne fonctionne pas',
        title: 'Page non trouvée',
        subheadline: "Oups, cette page n'est pas disponible!",
        paragraph:
            "La page que vous recherchez est introuvable. Veuillez retourner à la page d'accueil ou contacter notre service client.",
        button_homepage: "Retour à la page d'accueil",
        button_customer_support: 'Service client'
    }
}

// <Error> is rendered when:
//
// 1. A user requests a page that is not routable from `app/routes.jsx`.
// 2. A routed component throws an error in `getProps()`.
// 3. A routed component throws an error in `render()`.
//
// It must not throw an error. Keep it as simple as possible.

const Error = (props) => {
    const {message: errorMessage, stack} = props
    // display error message in console
    useEffect(() => {
        console.error(errorMessage, stack)
    }, [errorMessage, stack])

    const {locale, buildUrl} = useMultiSite()
    const {app} = getConfig()

    const messages = useMemo(() => {
        let selectedLocale = 'default'

        if (locale?.id?.includes('de')) {
            selectedLocale = 'de'
        } else if (locale?.id?.includes('fr')) {
            selectedLocale = 'fr'
        }

        return translations[selectedLocale]
    }, [locale])

    return (
        <Flex id="sf-app" flex={1} direction="column" minWidth={'375px'}>
            <Helmet>
                <title>{messages.pageTitle}</title>
            </Helmet>
            <Flex as="header" width="full" px={4} py="7px" justifyContent="center">
                <IconButton
                    as="a"
                    aria-label="logo"
                    icon={<BrandLogoBlack width={'5.51rem'} height="auto" />}
                    variant="unstyled"
                    href={buildUrl(HOME_HREF)}
                    height="full"
                />
            </Flex>
            <Flex
                as="main"
                id="app-main"
                role="main"
                direction="column"
                layerStyle="page"
                px={4}
                py={10}
                gap={10}
                textAlign="center"
            >
                <Flex direction="column" gap={4}>
                    <Text as="h2" textStyle="Semi Bold Medium">
                        {messages.title}
                    </Text>
                    <Text textStyle="Regular 2XLarge">{messages.subheadline}</Text>
                    <Text textStyle="Regular Medium">{messages.paragraph}</Text>
                </Flex>
                <Box className="error-log" display={app.displayErrorStackTrace ? 'block' : 'none'}>
                    {errorMessage && (
                        <Box
                            as="pre"
                            fontSize="sm"
                            background="gray.50"
                            borderColor="gray.200"
                            borderStyle="solid"
                            borderWidth="1px"
                            overflow="auto"
                            textAlign="left"
                            padding={4}
                        >
                            {errorMessage}
                        </Box>
                    )}
                    {stack && (
                        <Box
                            as="pre"
                            fontSize="sm"
                            background="gray.50"
                            borderColor="gray.200"
                            borderStyle="solid"
                            borderWidth="1px"
                            overflow="auto"
                            textAlign="left"
                            padding={4}
                        >
                            {stack}
                        </Box>
                    )}
                </Box>
                <Flex justifyContent="center">
                    <Image
                        src={getAssetUrl('static/img/page_not_found.png')}
                        width={{base: '290px', md: '376px', '2xl': '580px'}}
                    />
                </Flex>
                <Flex direction="column" gap={2} mt={10} alignItems="center">
                    <Button as="a" href={buildUrl(HOME_HREF)} width="full">
                        {messages.button_homepage}
                    </Button>
                    <Button
                        as="a"
                        target="_blank"
                        href={buildUrl(CONTACT_US_HREF)}
                        width="full"
                        variant="outline"
                    >
                        {messages.button_customer_support}
                    </Button>
                </Flex>
            </Flex>
        </Flex>
    )
}

Error.propTypes = {
    // JavaScript error stack trace: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/stack
    stack: PropTypes.string,
    // HTTP status code: https://developer.mozilla.org/en-US/docs/Web/HTTP/Status
    status: PropTypes.number,
    // A description of the error, if available
    message: PropTypes.string
}

export default Error
